import { Hours, WeekdayTitles } from "@/utils";
import { ChargerEntity, LocationEntity } from "@/domain/entities";
import { formatPhone } from "@/utils/Helper";
import { formatCurrency } from "../../utils/Helper";
import { Charger } from "./Charger";
import { Connector } from "./Connector";
import { LocationForm, LocationHour, LocationHourResponse } from "@/@types";
import { Partnership } from "./Partnership";
import { AMENITIES_KEYS, Amenity } from "./Amenity";

export class Location {
  entity?: LocationEntity;
  id: string;
  name: string;
  address: string;
  chargers_count: number;
  sessions_count: number;
  income: string;
  connectors_statuses: string[] = [];
  phone: string = "";
  coords: string;
  amenities: Amenity[] = [];
  images: string[] = [];
  chargers: Charger[] = [];
  hours: LocationHour[] = [];
  partnership?: Partnership | null

  constructor(location: LocationEntity) {
    this.entity = location;
    this.id = location.id;
    this.name = location.title;
    this.address = `${location.street}, ${location.number} - ${location.city}`;
    this.chargers_count = location.chargers_count || 0;
    this.sessions_count = location.sessions_count || 0;
    this.income = formatCurrency(location.sessions_sum_value || 0);

    if (location.phone) {
      this.phone = formatPhone(location.phone);
    }

    this.coords = `${location.lat}, ${location.lng}`;
    this.amenities = location.amenities ? location.amenities?.split("|").map(am => new Amenity(am)).filter(amenity => AMENITIES_KEYS[amenity.id]) : [];

    if (location.hours) {
      this.hours = Location.formatHours(location.hours);
    }

    if (location.images && location.images.length) {
      this.images = location.images.map(
        (elem) => `${process.env.VUE_APP_IMAGE_BASE_URL}${elem.path}`
      );
    }

    if (location.chargers) {
      this.connectors_statuses = Location.formatStatuses(location.chargers);
      this.chargers = location.chargers.map(
        (elem: ChargerEntity) => new Charger(elem)
      );
      this.chargers_count = location.chargers.length;
    }
    if (location.partnership_id && location.partnership) {
      this.partnership = new Partnership(location.partnership)
    }
  }

  static empty: Location = {
    id: "",
    name: "",
    address: "",
    chargers_count: 0,
    sessions_count: 0,
    income: "",
    connectors_statuses: [],
    phone: "",
    coords: "",
    amenities: [],
    images: [],
    chargers: [],
    hours: [],
  };

  static emptyForm: LocationForm = {
    title: "",
    description: "",
    cep: "",
    street: "",
    number: "",
    city: "",
    neighborhood: "",
    partnership_id: "",
    state: "",
    lat: "",
    lng: "",
    phone: "",
    amenities: [],
    hours: [
      {
        title: "Segunda-feira",
        weekday: "monday",
        open: false,
        twenty_four_hours: false,
        start_time: "",
        end_time: "",
      },
      {
        title: "Terça-feira",
        weekday: "tuesday",
        open: false,
        twenty_four_hours: false,
        start_time: "",
        end_time: "",
      },
      {
        title: "Quarta-feira",
        open: false,
        weekday: "wednesday",
        twenty_four_hours: false,
        start_time: "",
        end_time: "",
      },
      {
        title: "Quinta-feira",
        weekday: "thursday",
        open: false,
        twenty_four_hours: false,
        start_time: "",
        end_time: "",
      },
      {
        title: "Sexta-feira",
        weekday: "friday",
        open: false,
        twenty_four_hours: false,
        start_time: "",
        end_time: "",
      },
      {
        title: "Sábado",
        weekday: "saturday",
        open: false,
        twenty_four_hours: false,
        start_time: "",
        end_time: "",
      },
      {
        title: "Domingo",
        weekday: "sunday",
        open: false,
        twenty_four_hours: false,
        start_time: "",
        end_time: "",
      },
    ],
    images: [],
  };

  static formatStatuses = (charges: { connectors: { status: string }[] }[]) => {
    let statuses: string[] = [];
    for (const charger of charges) {
      statuses = [...statuses, ...Connector.formatStatuses(charger.connectors)];
    }
    return statuses;
  };

  static formatHours = (
    hours: Pick<
      LocationHourResponse,
      "weekday" | "end_time" | "start_time" | "twenty_four_hours"
    >[]
  ): LocationHour[] => {
    let hoursArr = [];
    for (let hour of Hours) {
      const item = hours.find((elem) => elem.weekday === hour.weekday);
      hoursArr.push({
        open: !!item,
        end_time: item?.end_time || "",
        start_time: item?.start_time || "",
        weekday: item?.weekday || hour.weekday,
        title: WeekdayTitles[item?.weekday || hour.weekday],
        twenty_four_hours: !!item?.twenty_four_hours,
      });
    }
    return hoursArr;
  };
}
